import React, { useState, useEffect, useCallback } from "react";
import {
  CreditCard,
  FileText,
  ExternalLink,
  Download,
  RefreshCw,
  Check,
  AlertCircle,
  Zap,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { AUTH_URL } from "../../constants";

const StatusBadge = ({ status }) => {
  const colors = {
    active: "bg-green-100 text-green-700",
    paid: "bg-green-100 text-green-700",
    cancelled: "bg-red-100 text-red-700",
    suspended: "bg-yellow-100 text-yellow-700",
    failed: "bg-red-100 text-red-700",
    unpaid: "bg-slate-100 text-slate-600",
  };
  return (
    <span
      className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || "bg-slate-100 text-slate-600"}`}
    >
      {status}
    </span>
  );
};

export default function ClientBillingView() {
  const { token } = useAuth();
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState("");
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchFacilities = useCallback(async () => {
    try {
      const res = await fetch(`${AUTH_URL}/my/facilities`, { headers });
      const data = await res.json();
      if (Array.isArray(data)) {
        setFacilities(data);
        if (data.length > 0 && !selectedFacility)
          setSelectedFacility(data[0].facility_id);
      }
    } catch (e) {
      console.error(e);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = useCallback(async () => {
    if (!selectedFacility) return;
    setLoading(true);
    try {
      const [subRes, invRes, usageRes] = await Promise.all([
        fetch(
          `${AUTH_URL}/billing/subscription?facility_id=${encodeURIComponent(selectedFacility)}`,
          { headers },
        ),
        fetch(
          `${AUTH_URL}/billing/invoices?facility_id=${encodeURIComponent(selectedFacility)}`,
          { headers },
        ),
        fetch(
          `${AUTH_URL}/billing/usage?facility_id=${encodeURIComponent(selectedFacility)}&period=30d`,
          { headers },
        ),
      ]);

      const subData = await subRes.json();
      if (!subData.error) setSubscription(subData);
      else setSubscription(null);

      const invData = await invRes.json();
      if (Array.isArray(invData)) setInvoices(invData);

      const usageData = await usageRes.json();
      if (!usageData.error) setUsage(usageData);
    } catch {
      /* ignore */
    }
    setLoading(false);
  }, [selectedFacility, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchFacilities();
  }, [fetchFacilities]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              Billing & Subscription
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              View your plan details and invoices
            </p>
          </div>
          <select
            value={selectedFacility}
            onChange={(e) => setSelectedFacility(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
          >
            {facilities.map((f) => (
              <option key={f.facility_id} value={f.facility_id}>
                {f.facility_name || f.display_name || f.facility_id}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Subscription Card */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-50 rounded-lg">
            <CreditCard className="w-5 h-5 text-blue-600" />
          </div>
          <h4 className="font-semibold text-slate-900">Current Plan</h4>
        </div>

        {subscription ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Plan</p>
              <p className="text-sm font-semibold text-slate-900">
                {subscription.product_name || "No active plan"}
              </p>
              <div className="flex gap-1 mt-2">
                {subscription.includes_rag && (
                  <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                    RAG
                  </span>
                )}
                {subscription.includes_slm && (
                  <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                    SLM
                  </span>
                )}
                {subscription.includes_private_llm && (
                  <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">
                    LLM
                  </span>
                )}
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Status</p>
              <StatusBadge
                status={subscription.subscription_status || "none"}
              />
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Monthly Price</p>
              <p className="text-sm font-semibold text-slate-900">
                {subscription.monthly_price
                  ? `$${Number(subscription.monthly_price).toFixed(2)}`
                  : "--"}
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Payment</p>
              <StatusBadge
                status={subscription.stripe_payment_status || "unpaid"}
              />
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">
            <AlertCircle className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm">No active subscription for this facility</p>
          </div>
        )}
      </div>

      {/* Usage Summary */}
      {usage && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-50 rounded-lg">
              <Zap className="w-5 h-5 text-green-600" />
            </div>
            <h4 className="font-semibold text-slate-900">Usage This Month</h4>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Conversations</p>
              <p className="text-xl font-bold text-slate-900">
                {(usage.total_conversations || 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Sessions</p>
              <p className="text-xl font-bold text-slate-900">
                {(usage.unique_sessions || 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Tokens Used</p>
              <p className="text-xl font-bold text-slate-900">
                {(
                  (usage.total_tokens_in || 0) + (usage.total_tokens_out || 0)
                ).toLocaleString()}
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Avg Response</p>
              <p className="text-xl font-bold text-slate-900">
                {Math.round(usage.avg_response_ms || 0)}ms
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Invoice List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-purple-50 rounded-lg">
            <FileText className="w-5 h-5 text-purple-600" />
          </div>
          <h4 className="font-semibold text-slate-900">Invoices</h4>
        </div>

        {invoices.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <FileText className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm">No invoices yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-2 text-slate-500 font-medium">
                    Date
                  </th>
                  <th className="text-left py-3 px-2 text-slate-500 font-medium">
                    Period
                  </th>
                  <th className="text-right py-3 px-2 text-slate-500 font-medium">
                    Amount
                  </th>
                  <th className="text-center py-3 px-2 text-slate-500 font-medium">
                    Status
                  </th>
                  <th className="text-right py-3 px-2 text-slate-500 font-medium">
                    Links
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr
                    key={inv.id}
                    className="border-b border-slate-100 hover:bg-slate-50"
                  >
                    <td className="py-3 px-2 text-slate-700">
                      {inv.created_at
                        ? new Date(inv.created_at).toLocaleDateString()
                        : "--"}
                    </td>
                    <td className="py-3 px-2 text-slate-600">
                      {inv.period_start
                        ? new Date(inv.period_start).toLocaleDateString()
                        : "--"}{" "}
                      &ndash;{" "}
                      {inv.period_end
                        ? new Date(inv.period_end).toLocaleDateString()
                        : "--"}
                    </td>
                    <td className="py-3 px-2 text-right font-medium text-slate-900">
                      ${((inv.amount_cents || 0) / 100).toFixed(2)}
                    </td>
                    <td className="py-3 px-2 text-center">
                      <StatusBadge status={inv.status} />
                    </td>
                    <td className="py-3 px-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {inv.hosted_invoice_url && (
                          <a
                            href={inv.hosted_invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700"
                            title="View"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                        {inv.pdf_url && (
                          <a
                            href={inv.pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-slate-500 hover:text-slate-700"
                            title="PDF"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
